import React from 'react';
import UnitPageShell from '../../components/shared/UnitPageShell';
import EditableTextBlock from '../../components/shared/EditableTextBlock';
import ZoomableImage from '../../components/shared/ZoomableImage';
import { Box, Button, IconButton } from '@mui/material';
import { VolumeUp as SpeakerIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTTS } from '../../hooks/useTTS';
import { uploadUnitAsset, updateUnitContent, useUnitOverride } from '../../services/unitOverridesService';

const PAGE_ID = 'm1_u1_x5_befeuchtung';

const M1_U1_X5_Befeuchtung: React.FC = () => {
  const navigate = useNavigate();
  const { token, canEditTextsM1, canUploadImagesM1 } = useAuth();
  const { override, refresh } = useUnitOverride(PAGE_ID);

  const textBlocks = React.useMemo(() => ([
    { id: 'block_anlagenelemente', heading: 'Anlagenelemente', text: override?.texts?.['block_anlagenelemente'] ?? 'Klimaanlage mit Mischluftklappen, Vorerhitzer, Nacherhitzer, Kühler und Luftbefeuchter. Zu- und Abluftventilator, Zuluftfilter.' },
    { id: 'block_regelung', heading: 'Regelung', text: override?.texts?.['block_regelung'] ?? 'Gleitende Temperatur-Vorregelung, Umschaltung auf Festwertregelung im Kühlbetrieb. Sequenz Mischluftklappen-Vorerhitzer mit Energieauswahl. Abluft-Zulufttemperatur-Kaskade mit Min-/Max-Begrenzung. Sollwertgleitung nach DIN. Abluftfeuchte-Regelung mit Maximalbegrenzung der Zuluftfeuchte. Anfahrschaltung, Frostschutz, Stillstandsregelung, Pumpenblockierschutz.' },
    { id: 'block_funktionsbeschreibung', heading: 'Funktionsbeschreibung', text: override?.texts?.['block_funktionsbeschreibung'] ?? 'Anlage durch Zeitprogramme. Anfahrschaltung öffnet Y1 und startet P1. Freigabe nach Rücklauftemp. B7/K17 und Ventilator-Rückmeldung K01. Vorregelung: B4 vergleicht mit XS1, steuert Y2 dann Y1. Kühl-/Entfeuchtbetrieb: Umschaltung XS1.1 auf XS1.2 (12°C). Energieauswahl: Vergleich B3 vs. B1. Kaskade: B3 Führungs-, B2 Regelgröße; Y4/Y5 sequenziell. Feuchte: B14 vs. XS3 über Y6/Y5, B13 Maximalbegrenzung. Frostschutz K9: Ventis aus, Klappen zu, Y1 auf, P1 ein; Stillstandsregelung hält Mindesttemp. Filterüberwachung K12; Intervallschaltung gegen Blockieren.' }
  ]), [override]);

  const fullPlainText = React.useMemo(() => {
    return textBlocks.map(b => `${b.heading}. ${b.text}`).join(' ');
  }, [textBlocks]);

  const { speak, stop, speaking } = useTTS({ text: fullPlainText });

  const handleImageReplace = React.useCallback(async (imageKey: string, file: File) => {
    if (!token) return alert('Kein Token – bitte einloggen.');
    if (!canUploadImagesM1) return alert('Keine Berechtigung fuer Bilder-Uploads.');
    try {
      await uploadUnitAsset(PAGE_ID, imageKey, file, token);
      alert('Bild erfolgreich aktualisiert.');
      refresh();
    } catch (e: any) {
      alert('Upload fehlgeschlagen: ' + (e?.message || e));
    }
  }, [token, refresh, canUploadImagesM1]);

  const handleTextSave = React.useCallback(async (blockId: string, newBody: string) => {
    if (!token) return alert('Kein Token – bitte einloggen.');
    if (!canEditTextsM1) return alert('Keine Berechtigung zum Texte bearbeiten.');
    try {
      await updateUnitContent(PAGE_ID, { texts: { [blockId]: newBody } }, token);
      alert('Text gespeichert.');
      refresh();
    } catch (e: any) {
      alert('Speichern fehlgeschlagen: ' + (e?.message || e));
    }
  }, [token, refresh, canEditTextsM1]);

  const ttsButton = React.createElement(IconButton, {
    onClick: speaking ? stop : speak,
    'aria-label': 'Text vorlesen',
    sx: { ml: 1 }
  }, React.createElement(SpeakerIcon, { sx: { fontSize: 28 } }));

  return React.createElement(
    UnitPageShell,
    { title: '1.1.5 Be-/Entfeuchtung', actions: ttsButton },
    React.createElement(Box, { sx: { display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 } },
      React.createElement(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 2 } },
        React.createElement(ZoomableImage, {
          src: (() => { const v = override?.images?.['img_0']; if (!v) return '/assets/drawable/klimaregelung_mit_erh_nerh_kue_bef.PNG'; const s = String(v); return s.startsWith('/assets/') || s.startsWith('assets/') ? s : `/assets/unit_custom/${s}`; })(),
          alt: 'Klimaanlage mit Be-/Entfeuchtung',
          label: 'Bild einfügen',
          onReplace: canUploadImagesM1 ? (file: File) => handleImageReplace('img_0', file) : undefined
        })
      ),
      React.createElement(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 2 } },
        textBlocks.map((block) => React.createElement(EditableTextBlock, {
          key: block.id,
          pageId: PAGE_ID,
          blockId: block.id,
          heading: block.heading,
          body: block.text,
          onSave: (newBody: string) => handleTextSave(block.id, newBody)
        })),
        React.createElement(Box, { sx: { mt: 3 } },
          React.createElement(Button, { variant: 'outlined', onClick: () => navigate('/units/m1_u1_x99_quiz') }, 'Zum Quiz')
        )
      )
    )
  );
};

export default M1_U1_X5_Befeuchtung;

